import React, { useState, useEffect } from 'react';
import { useSelector, dispatch, useDispatch } from 'react-redux';
import { Table, Icon } from 'antd';
import { getRevenueReportData, getSearchResults, getSearchResultsAsLists, clearSearchResults, clearSearchLists } from '../ReportAction';
import {
  revenueTrackerObj,
  searchTextOnFilter,
  searchOnFilter,
  handleNullValues,
  searchFilterForEmptyFields,
  revenueTrackerSearchResultObj,
  revenueTrackerObjList,
  setToolTip,
} from '../../../helper/ReportingHelper';
import SearchText from '../common/SearchText';
import SearchFilters from '../common/SearchFilters';
import YearMonthFilter from '../common/YearMonthFilter';
import DateFilter from '../common/DateFilter';
import InfiniteTable from '../../Common/Table/InfiniteTable';
import withTracker from '../HOCs/withTracker';
import HeaderButton from '../common/HeaderButton';
import decode from 'jwt-decode';

const RevenueTracker = ({ handleDownload, status }) => {
  const dispatch = useDispatch();
  const [dataSource, setDataSource] = useState([]);
  const [searchingKey, setSearchingKey] = useState('');
  const [dataSourcesLists, setDataSourcesLists] = useState([]);
  const [tableData, setTableData] = useState({});
  const [searchingStatus, setSearchingStatus] = useState(false);
  const [searchStatusList, setSearchStatusList] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const revenueData = useSelector((state) => state?.report?.reports?.revenue?.data);
  const dataloading = useSelector((state) => state?.report?.reports?.revenue?.loading);
  const searchLoading = useSelector((state) => state?.report?.loading || false);
  const searchFetching = useSelector((state) => state?.report?.fetching || false);
  const authToken = useSelector((state) => state?.auth?.loginDetails?.data?.token);
  const revenueTrackerSearchResult = useSelector((state) => state?.report?.searchResult || {});
  const revenueTrackerSearchResultList = useSelector((state) => state?.report?.searchResultList || []); 
  const loggedUser = authToken ? decode(authToken) : '';
  const isSuperAdmin = loggedUser.authorities && (loggedUser.authorities[0] === 'role.suadmin') ? true : false;

  const AuthTokenData = decode(authToken);
  const userCountry = AuthTokenData?.location ?.country;

  useEffect(() => {
    const formattedData = revenueTrackerObj(revenueData);
    formattedData?.length && setDataSource(formattedData);
    console.log('rev data change',revenueData);
    //formattedData?.length && setLoading(false);
    // if(!revenueData.length)
    // setLoading(false)
  }, [revenueData]);
  
  useEffect(() => {
      setDataLoading(dataloading || searchLoading || searchFetching)
  }, [dataloading, searchLoading, searchFetching]);

  useEffect(() => {
    if (revenueTrackerSearchResultList.length) {
      console.log(`Search results received: ${revenueTrackerSearchResultList.length} items`);
      const formattedDataLists = revenueTrackerObjList(revenueTrackerSearchResultList);
      formattedDataLists?.length && setDataSourcesLists(formattedDataLists);
    } else if (searchStatusList) {
      console.log('Search completed but no results returned from API');
    }
  }, [revenueTrackerSearchResultList, searchStatusList]);

  const handleSearch = (setSelectedKeys, confirm, searchText, type, mainType, searchTextKey) => {
    const startDate = searchText[0];
    const endDate = searchText[searchText.length - 1];
    // Clear previous search states and results when starting a new search
    setSearchStatusList(false);
    dispatch(clearSearchLists()); // Clear list search results immediately
    setSearchingKey(searchTextKey);
    dispatch(getSearchResults(searchText, type, mainType, startDate, endDate));
    setSearchingStatus(true);
  };

  const handleReset = (setSelectedKeys, clearFilters) => {
    setSearchingStatus(false);
    setSearchStatusList(false);
    setSearchingKey('');
    setDataSourcesLists([]);
    dispatch(clearSearchResults());
    dispatch(clearSearchLists());
    setSelectedKeys([]);
    clearFilters();
  };

  const handleSearchList = (setSelectedKeys, confirm, searchText, type, mainType) => {
    let startDate = '';
    let endDate = '';
    if (type === 'date' || type === 'paymentDate') {
      startDate = searchText[0];
      endDate = searchText[searchText.length - 1];
    }
    
    console.log(`Starting search for: "${searchText}" (type: ${type})`);
    
    // Clear previous search states and results when starting a new search
    setSearchingStatus(false);
    setDataSourcesLists([]); // Clear local state immediately
    dispatch(clearSearchResults()); // Clear single search results immediately
    setSearchingKey(searchText); // Store the actual search text instead of type
    dispatch(getSearchResultsAsLists(searchText, type, mainType, startDate, endDate));
    setSearchStatusList(true);
  };

  const handleResetList = (setSelectedKeys, clearFilters) => {
    setSearchStatusList(false);
    setSearchingStatus(false);
    setSearchingKey('');
    setDataSourcesLists([]);
    dispatch(clearSearchResults());
    dispatch(clearSearchLists());
    setSelectedKeys([]);
    clearFilters();
  };

  const handleFilterSearch = (setSelectedKeys, confirm, searchText) => {
    setSelectedKeys([searchText]);
    confirm();
  };

  const handleFilterReset = (setSelectedKeys, clearFilters) => {
    setSearchingStatus(false);
    setSearchStatusList(false);
    setSearchingKey('');
    setDataSourcesLists([]);
    dispatch(clearSearchResults());
    dispatch(clearSearchLists());
    setSelectedKeys([]);
    clearFilters();
  };

  const checkObj = (searchingKey) => {
    let searchText1 = searchingKey;

    let res = [];
    if (revenueTrackerSearchResult.hasOwnProperty(searchText1) && revenueTrackerSearchResult[searchText1] === null) {
      res = []; 
    } else {
      res = [revenueTrackerSearchResultObj(revenueTrackerSearchResult)];
    }

    return res;
  };

  const getTableDataSource = () => {
    // If search is loading, show empty array to display loading indicator
    if (searchLoading || searchFetching) {
      return [];
    }
    
    // Show appropriate search results or main data
    if (searchingStatus) {
      const searchResults = checkObj(searchingKey);
      return searchResults;
    }
    
    if (searchStatusList) {
      // If search returned empty results but we have a search term, 
      // try to filter local data as fallback
      if (dataSourcesLists.length === 0 && searchingKey) {
        const fallbackResults = performLocalSearch(dataSource, searchingKey);
        if (fallbackResults.length > 0) {
          console.log(`API search returned no results for "${searchingKey}", using local fallback`);
          return fallbackResults;
        }
      }
      return dataSourcesLists;
    }
    
    return dataSource;
  };

  const performLocalSearch = (data, searchTerm) => {
    if (!searchTerm || !data) return [];
    
    const searchLower = searchTerm.toLowerCase().trim();
    return data.filter(item => {
      const guestName = item.guestName ? item.guestName.toLowerCase() : '';
      return guestName.includes(searchLower);
    });
  };

  const revenueHeaders = [
    {
      key: 'bookingRef',
      title: 'Booking Ref.#',
      dataIndex: 'bookingRef',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, type, mainType, searchTextKey }) => (
        <SearchText
          placeholder="Search"
          type="text"
          onSearch={(searchText) =>
            handleSearch(setSelectedKeys, confirm, searchText, (type = 'booking'), (mainType = 'revenue-tracker'), (searchTextKey = 'bookingId'))
          }
          onReset={() => handleReset(setSelectedKeys, clearFilters)}
        />
      ),
      filterIcon: (filtered) => {
        return <Icon type="search" title="Search" style={{ color: filtered ? '#1890ff' : undefined, left: '62px' }}></Icon>;
      },
      onFilter: (value, record) => {
        return searchTextOnFilter(value, record, 'bookingRef');
      },
    },
    {
      key: 'category',
      title: 'Category',
      dataIndex: 'category',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <SearchFilters
          filterType="category"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '68px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'category');
      },
    },
    {
      key: 'activity',
      title: 'Activity',
      dataIndex: 'activity',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <SearchFilters
          filterType="activity"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '59px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'activity');
      },
    },
    {
      key: 'guestName',
      title: 'Guest Name',
      dataIndex: 'guestName',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, mainType, type, startDate, endDate }) => (
        <SearchText
          placeholder="Search"
          type="text"
          onSearch={(searchText) =>
            handleSearchList(
              setSelectedKeys,
              confirm,
              searchText,
              (type = 'guestName'),
              (mainType = 'revenue-tracker'),
              (startDate = ''),
              (endDate = '')
            )
          }
          onReset={() => handleResetList(setSelectedKeys, clearFilters)}
        />
      ),
      filterIcon: (filtered) => {
        return <Icon type="search" title="Search" style={{ color: filtered ? '#1890ff' : undefined, left: '47px' }}></Icon>;
      },
      onFilter: (value, record) => {
        return searchTextOnFilter(value, record, 'guestName');
      },
    },
    {
      key: 'guestEmail',
      title: 'Guest Email',
      dataIndex: 'guestEmail',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'hostName',
      title: 'Host Name',
      dataIndex: 'hostName',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'hostEmail',
      title: 'Host Email',
      dataIndex: 'hostEmail',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    
    {
      key: 'spaceId',
      title: 'Space ID',
      dataIndex: 'spaceId',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, type, mainType, startDate, endDate }) => (
        <SearchText
          placeholder="Search"
          type="text"
          onSearch={(searchText) =>
            handleSearchList(
              setSelectedKeys,
              confirm,
              searchText,
              (type = 'spaceId'),
              (mainType = 'revenue-tracker'),
              (startDate = ''),
              (endDate = '')
            )
          }
          onReset={() => handleResetList(setSelectedKeys, clearFilters)}
        />
      ),
      filterIcon: (filtered) => {
        return <Icon type="search" title="Search" style={{ color: filtered ? '#1890ff' : undefined, left: '65px' }}></Icon>;
      },
      onFilter: (value, record) => {
        return searchTextOnFilter(value, record, 'spaceId');
      },
    },
    {
      key: 'spaceName',
      title: 'Space Name',
      dataIndex: 'spaceName',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
        mainType = 'revenue-tracker',
        type = 'spaceName',
        startDate = '',
        endDate = '',
      }) => (
        <SearchText
          placeholder="Search"
          type="text"
          onSearch={(searchText) =>
            handleSearchList(
              setSelectedKeys,
              confirm,
              searchText,
              (type = 'spaceName'),
              (mainType = 'revenue-tracker'),
              (startDate = ''),
              (endDate = '')
            )
          }
          onReset={() => handleResetList(setSelectedKeys, clearFilters)}
        />
      ),
      filterIcon: (filtered) => {
        return <Icon type="search" title="Search" style={{ color: filtered ? '#1890ff' : undefined, left: '82px' }}></Icon>;
      },
      onFilter: (value, record) => {
        return searchTextOnFilter(value, record, 'spaceName');
      },
    },
    {
      key: 'spaceCountry',
      title: 'Country',
      dataIndex: 'spaceCountry',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return text === 'LK' ? 'SL' : handleNullValues(text);
      },
      filterDropdown: isSuperAdmin  ? ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <SearchFilters
          filterType="country"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ):null,
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '61px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'spaceCountry');
      },
    },
    {
      key: 'bookingDate',
      title: 'Booking Date',
      dataIndex: 'bookingDate',
      width: 113,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, mainType = 'revenue-tracker', type = 'date', startDate, endDate }) => (
        <DateFilter
          onReset={() => handleResetList(setSelectedKeys, clearFilters)}
          onSearch={(searchText) =>
            handleSearchList(setSelectedKeys, confirm, searchText, (type = 'date'), (mainType = 'revenue-tracker'), startDate, endDate)
          }
        />
      ),
      filterIcon: (filtered) => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined, left: '65px' }} />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'bookingDate');
      },
    },
    {
      key: 'paymentDate',
      title: 'Payment Date',
      dataIndex: 'paymentDate',
      className: 'custom-width',
      width: 113,
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, type, mainType, startDate, endDate }) => (
        <DateFilter
          onReset={() => handleResetList(setSelectedKeys, clearFilters)}
          onSearch={(searchText) =>
            handleSearchList(setSelectedKeys, confirm, searchText, (type = 'paymentDate'), (mainType = 'revenue-tracker'), startDate, endDate)
          }
        />
      ),
      filterIcon: (filtered) => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined, left: '66px' }} />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'paymentDate');
      },
    },
    {
      key: 'eventStartDate',
      title: 'Event Start Date',
      dataIndex: 'eventStartDate',
      width: 113,
    },
    {
      key: 'eventEndDate',
      title: 'Event End Date',
      dataIndex: 'eventEndDate',
      width: 113,
    },
    {
      key: 'month',
      title: 'Month',
      dataIndex: 'month',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <YearMonthFilter
          filterType="month"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '50px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'month');
      },
    },
    {
      key: 'year',
      title: 'Year',
      dataIndex: 'year',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <YearMonthFilter
          filterType="year"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '39px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchOnFilter(value, record, 'year');
      },
    },
    {
      key: 'baseBookingValue',
      title: 'Base Booking Value',
      dataIndex: 'baseBookingValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'hostDiscountValue',
      title: 'Host Discount Value',
      dataIndex: 'hostDiscountValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'bookingValueAfterHostDiscount',
      title: 'Booking Value After Host Discount',
      dataIndex: 'bookingValueAfterHostDiscount',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'taxPayableToHost',
      title: 'Tax Payable to Host',
      dataIndex: 'taxPayableToHost',
      width: 113,
      render: (text) => {
        return text ? text.toFixed(2) : handleNullValues(text);
      },
    },
    {
      key: 'msDiscountValue',
      title: 'MS Discount Value',
      dataIndex: 'msDiscountValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'finalBookingValue',
      title: 'Final Booking Value',
      dataIndex: 'finalBookingValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'taxPayableByGuest',
      title: 'Tax Payable by Guest',
      dataIndex: 'taxPayableByGuest',
      width: 113,
      render: (text) => {
        return text ? text.toFixed(2) : handleNullValues(text);
      },
    },
    {
      key: 'finalGuestPaymentAmount',
      title: 'Final Guest Payment Amount',
      dataIndex: 'finalGuestPaymentAmount',
      width: 113,
      render: (text) => {
        return text ? text.toFixed(2) : handleNullValues(text);
      },
    },
    {
      key: 'transactionFee',
      title: 'Transaction Fee',
      dataIndex: 'transactionFee',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'msCommission',
      title: 'MS Commission %',
      dataIndex: 'msCommission',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'commissionValue',
      title: 'Commission Value',
      dataIndex: 'commissionValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'finalCommissionValue',
      title: 'Final Commission Value',
      dataIndex: 'finalCommissionValue',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'totalPayableToHost',
      title: 'Total Payable To Host',
      dataIndex: 'totalPayableToHost',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'grossProfit',
      title: 'Gross Profit',
      dataIndex: 'grossProfit',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
    },
    {
      key: 'paymentStatus',
      title: 'Payment Status',
      dataIndex: 'paymentStatus',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <SearchFilters
          filterType="paymentStatus"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '66px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchFilterForEmptyFields(value, record, 'paymentStatus');
      },
    },
    {
      key: 'bookingStatus',
      title: 'Booking Status',
      dataIndex: 'bookingStatus',
      className: 'custom-width',
      width: 113,
      render: (text) => {
        return handleNullValues(text);
      },
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <SearchFilters
          filterType="bookingStatus"
          onChange={(filterValue) => {
            if (filterValue.length) {
              handleFilterSearch(setSelectedKeys, confirm, filterValue);
            } else {
              handleFilterReset(setSelectedKeys, clearFilters);
            }
          }}
        />
      ),
      filterIcon: (filtered) => <Icon type="filter" style={{ color: filtered ? '#1890ff' : undefined, left: '63px' }} theme="filled" />,
      onFilter: (value, record) => {
        return searchFilterForEmptyFields(value, record, 'bookingStatus');
      },
    },
  ];

  const downloadCSV = () => {
    handleDownload({ ...tableData, reportType: 'revenue-tracker', totalDataLength: dataSource?.length, token: authToken, spaceCountry: userCountry });
  };

  return (
    <div>
      <HeaderButton handleDownload={downloadCSV} status={status} />

      <InfiniteTable
        columns={revenueHeaders}
        dataSource={getTableDataSource()}
        tableDispatch={getRevenueReportData}
        props={{ increaseValue: 20, token: authToken }}
        tableData={setTableData}
        scroll={{ y: 330, x: 500 }}
        searchStatus={searchingStatus}
        searchStatusList={searchStatusList}
        loading={dataLoading}
      />
    </div>
  );
};

export default withTracker('REVENUE_TRACKER')(RevenueTracker);
